/**
 * API response for a single prompt template (snake_case matching backend).
 */
export interface PromptTemplateApiResponse {
  id: string
  tenant_id: string
  user_id: string
  name: string
  description: string
  content: string
  form: Record<string, unknown> | null
  agency_slug: string | null
  created_at: string
  updated_at: string
}

/**
 * Paginated list response for prompt templates.
 */
export interface PromptTemplateListApiResponse {
  items: PromptTemplateApiResponse[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

/**
 * Payload for creating a new prompt template.
 */
export interface PromptTemplateCreatePayload {
  name: string
  content: string
  description?: string
  form?: Record<string, unknown> | null
  agency_slug?: string
}

/**
 * Payload for updating an existing prompt template.
 */
export interface PromptTemplateUpdatePayload {
  name?: string
  content?: string
  description?: string
  form?: Record<string, unknown> | null
  agency_slug?: string
}

/**
 * Query parameters for listing prompt templates (tenant-wide).
 */
export interface PromptTemplateListParams {
  page?: number
  pageSize?: number
}

/**
 * Query parameters for listing prompt templates scoped to an agency.
 */
export interface PromptTemplateAgencyListParams {
  agencySlug: string
  page?: number
  pageSize?: number
}

/**
 * Frontend-friendly prompt template model (camelCase).
 * Metadata fields (tenantId, userId, agencySlug) are optional as they are
 * only set from API responses and not required for UI display.
 */
export interface PromptTemplate {
  id: string
  name: string
  description: string
  content: string
  form: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
  tenantId?: string
  userId?: string
  agencySlug?: string | null
}

/**
 * Transformed paginated list response with camelCase items.
 */
export interface PromptTemplateListResponse {
  itemList: PromptTemplate[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

/**
 * Converts a single API response to the frontend PromptTemplate model.
 */
export const transformPromptTemplateFromApi = (
  response: PromptTemplateApiResponse
): PromptTemplate => ({
  id: response.id,
  tenantId: response.tenant_id,
  userId: response.user_id,
  name: response.name,
  description: response.description,
  content: response.content,
  form: response.form,
  agencySlug: response.agency_slug,
  createdAt: response.created_at,
  updatedAt: response.updated_at,
})

/**
 * Converts a paginated API list response to the frontend model.
 */
export const transformPromptTemplateListFromApi = (
  response: PromptTemplateListApiResponse
): PromptTemplateListResponse => ({
  itemList: response.items.map(transformPromptTemplateFromApi),
  total: response.total,
  page: response.page,
  pageSize: response.page_size,
  hasMore: response.has_more,
})
